import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { DataGrid } from '@/components/data-grid';
import type { ColumnFilterGroup } from '@/components/data-grid';
import { ROUTES } from '@/app/routes';
import type { UserMgmtResponseDTO } from '@/api/generated/model';
import { userTypeLabel, LOGIN_TYPE_FILTER_OPTIONS } from '@/lib/enums';
import { useDashboardOverview } from '@/features/dashboard/api';
import { userListMetricsFromOverview } from '@/features/dashboard/platformMetrics';
import { useUsersList } from '../api';
import { userColumns } from '../columns';
import { MetricCardsRow } from '../components/MetricCardsRow';

// Columns still filtered CLIENT-side, how to read the value off a row, and how to label the raw
// code in the dropdown (the option `key` stays the raw code so filtering still matches). Login
// Type and Location are not here — they filter server-side, see SERVER_FILTER_KEYS below.
const FILTERABLE: {
  columnKey: string;
  label: string;
  get: (r: UserMgmtResponseDTO) => string | null | undefined;
  fmt?: (v: string) => string;
}[] = [{ columnKey: 'type', label: 'Type', get: (r) => r.userType, fmt: userTypeLabel }];

// Filters the search endpoint applies itself; their selections go to useServerTable (which puts
// them in the query key and refetches) instead of the local client-side state.
const SERVER_FILTER_KEYS = new Set(['loginType', 'location']);

const LOGIN_TYPE_GROUP: ColumnFilterGroup = {
  columnKey: 'loginType',
  label: 'Login Type',
  options: LOGIN_TYPE_FILTER_OPTIONS,
};

export function UserListPage() {
  const navigate = useNavigate();
  const table = useUsersList();
  const overview = useDashboardOverview();
  const listMetrics = useMemo(
    () => userListMetricsFromOverview(overview.data),
    [overview.data],
  );
  const rows = table.rows;

  // Type still filters over the LOADED page only — the search endpoint has no field for it.
  // Login Type and Location live in table.filters and are applied by the server.
  const [clientFilters, setClientFilters] = useState<Record<string, string[]>>({});

  // users.location is free text (no code list to hard-code), and it is filtered server-side, so its
  // options cannot come from the current page: once a location is selected the page holds only that
  // one value. Accumulate every location seen so far instead — the set only ever grows, so a
  // selected option never vanishes from under the user.
  const seenLocations = useRef<Set<string>>(new Set());
  const [locationOptions, setLocationOptions] = useState<string[]>([]);
  useEffect(() => {
    let added = false;
    for (const r of rows) {
      const v = r.location?.trim();
      if (v && !seenLocations.current.has(v)) {
        seenLocations.current.add(v);
        added = true;
      }
    }
    if (added) setLocationOptions([...seenLocations.current].sort());
  }, [rows]);

  const filterGroups = useMemo<ColumnFilterGroup[]>(
    () => [
      ...FILTERABLE.map(({ columnKey, label, get, fmt }) => {
        const seen = new Set<string>();
        for (const r of rows) {
          const v = get(r);
          if (v) seen.add(String(v));
        }
        return {
          columnKey,
          label,
          options: [...seen].sort().map((v) => ({ key: v, label: fmt ? fmt(v) : v })),
        };
      }),
      LOGIN_TYPE_GROUP,
      {
        columnKey: 'location',
        label: 'Location',
        options: locationOptions.map((v) => ({ key: v, label: v })),
      },
    ],
    [rows, locationOptions],
  );

  // One object for the grid; writes are routed to whichever store owns each key.
  const selectedFilters = useMemo(
    () => ({
      ...clientFilters,
      loginType: table.filters.loginType ?? [],
      location: table.filters.location ?? [],
    }),
    [clientFilters, table.filters],
  );

  const handleFiltersChange = (next: Record<string, string[]>) => {
    const client: Record<string, string[]> = {};
    const server: Record<string, string[]> = {};
    for (const [key, vals] of Object.entries(next)) {
      (SERVER_FILTER_KEYS.has(key) ? server : client)[key] = vals;
    }
    setClientFilters(client);
    table.setFilters(server);
  };

  const filteredRows = useMemo(() => {
    const active = Object.entries(clientFilters).filter(([, vals]) => vals.length > 0);
    if (active.length === 0) return rows;
    const getters = new Map(FILTERABLE.map((f) => [f.columnKey, f.get]));
    return rows.filter((r) =>
      active.every(([col, vals]) => {
        const get = getters.get(col);
        const field = get?.(r);
        return field != null && vals.includes(String(field));
      }),
    );
  }, [rows, clientFilters]);

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">User Management</h1>
        <div className="mt-4">
          {overview.isLoading ? (
            <div className="flex h-16 items-center justify-center">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <MetricCardsRow metrics={listMetrics} />
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <DataGrid<UserMgmtResponseDTO>
          title="Users"
          searchPlaceholder="Search users…"
          columns={userColumns}
          rows={filteredRows}
          total={table.total}
          isLoading={table.isLoading}
          isFetchingNextPage={table.isFetchingNextPage}
          isError={table.isError}
          error={table.error}
          endReached={table.endReached}
          page={table.page}
          totalPage={table.totalPage}
          onPageChange={table.setPage}
          isFetching={table.isFetching}
          onLoadMore={table.fetchNextPage}
          onRetry={() => void table.refetch()}
          search={table.search}
          onSearchChange={table.setSearch}
          filterGroups={filterGroups}
          selectedFilters={selectedFilters}
          onFiltersChange={handleFiltersChange}
          onRowClick={(row) => void navigate(ROUTES.userDetails(String(row.id)))}
          getRowId={(row) => String(row.id)}
          emptyMessage="No users"
        />
      </div>
    </div>
  );
}
